import React, { useState } from 'react';
import {
  Button,
  Checkbox,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import { ViewColumn as ColumnsIcon } from '@mui/icons-material';
import type { ColumnDef } from '../../utils/columnVisibility';

/**
 * REPORTING-CENTER.2.1 — "Columns" show/hide menu for report tables.
 *
 * A small outlined button that opens a checkbox menu of the toggleable
 * columns. Locked columns (identity/time anchors) are listed disabled+checked
 * so users see they exist but can't hide them. Visibility state + persistence
 * live in the caller via useColumnVisibility; this is pure presentation.
 */

interface Props {
  columns: ColumnDef[];
  visible: Record<string, boolean>;
  onToggle: (key: string) => void;
  onReset?: () => void;
  disabled?: boolean;
}

const ColumnVisibilityMenu: React.FC<Props> = ({ columns, visible, onToggle, onReset, disabled }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const hiddenCount = columns.filter(c => !c.locked && !visible[c.key]).length;

  return (
    <>
      <Button
        variant="outlined"
        size="medium"
        startIcon={<ColumnsIcon />}
        onClick={e => setAnchorEl(e.currentTarget)}
        disabled={disabled}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        Columns{hiddenCount > 0 ? ` (${hiddenCount} hidden)` : ''}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {columns.map(col => (
          <MenuItem
            key={col.key}
            dense
            disabled={col.locked}
            onClick={() => !col.locked && onToggle(col.key)}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Checkbox
                edge="start"
                size="small"
                checked={visible[col.key] ?? false}
                disableRipple
                tabIndex={-1}
                sx={{ p: 0 }}
              />
            </ListItemIcon>
            <ListItemText primary={col.label} />
          </MenuItem>
        ))}
        {onReset && (
          <>
            <Divider />
            <MenuItem dense onClick={() => { onReset(); setAnchorEl(null); }}>
              <ListItemText primary="Reset to defaults" sx={{ pl: '36px' }} />
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
};

export default ColumnVisibilityMenu;
